/**
 * The guided tutorial: what each step says (texts in the i18n catalogues, `tour.<id>.*`) and the
 * demo it plays. Demos drive the real interface — they open the real menus, click the real
 * buttons and draw on the real sheet through the editor — so what the user sees is exactly what
 * they will do themselves.
 */
import type { Id, Point, Project } from '@elec/domain';
import type { Editor } from '@elec/editor';
import type { MessageKey } from '@elec/i18n';
import { pageLayout } from '@elec/layout';
import { getSymbol } from '@elec/symbols';
import type { PanelTab } from './BottomPanel.tsx';
import type { SideSection, SidebarState } from './Sidebar.tsx';

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** What the tutorial needs from the app. */
export interface TourApp {
  editor: Editor;
  /** Sheet millimetres → client pixels. */
  toClient(x: number, y: number): Point;
  fitPage(): void;
  /** Zoom to an area of the sheet, in millimetres. */
  zoomToArea(b: { minX: number; minY: number; maxX: number; maxY: number }): void;
  panel(): PanelTab | null;
  setPanel(p: PanelTab | null): void;
  /** The right sidebar: folded or not, its open sections. */
  sidebar(): SidebarState;
  setSidebar(s: SidebarState): void;
  /** The left symbol palette: folded to a rail of icons or not. */
  paletteFolded(): boolean;
  setPaletteFolded(f: boolean): void;
  /** Close any open dialog (project information, templates…). */
  closeDialogs(): void;
}

/** Something to highlight: a CSS selector, or a rectangle computed from the app (on the sheet). */
export type SpotFn = (app: TourApp) => Rect | null;
export type Spot = string | SpotFn;

/** The demo's hands: every action is animated, and instant when the user skips ahead. */
export interface DemoCtx {
  app: TourApp;
  /** Ids of what earlier demos created (the supply, the RCD…), by role. */
  memo: Record<string, Id>;
  /** Highlight something (null: nothing), with an optional caption next to it. */
  spot(s: Spot | null, caption?: MessageKey): void;
  wait(ms: number): Promise<void>;
  /** Move the cursor onto an element (selector) and click it. False when it is not on screen. */
  click(selector: string): Promise<boolean>;
  /** Click a field and type into it, one character at a time. */
  type(selector: string, text: string, opts?: { commit?: boolean }): Promise<boolean>;
  /** Set a field at once, without animation (to put back what an earlier part typed). */
  fill(selector: string, text: string): void;
  /** Pick a value in a drop-down list. */
  choose(selector: string, value: string): Promise<boolean>;
  /** Show a key being pressed (the action itself is done by the demo). */
  key(label: string): Promise<void>;
  /** Close an open menu. */
  closeMenus(): void;
  /** Pick a symbol in the palette and place it on the sheet. Returns the new element. */
  place(symbolId: string, at: Point): Promise<Id | undefined>;
  /** Draw a wire with the wire tool through these sheet points (ports, wires or grid). */
  wire(points: Point[]): Promise<void>;
  /** Click on the sheet with the select tool. */
  sheetClick(p: Point): Promise<void>;
  /** Drag on the sheet with the select tool (moves what is under `from`). */
  drag(from: Point, to: Point): Promise<void>;
  /** Sample data typed by the demo, in the interface language. */
  sample(k: 'title' | 'owner' | 'address' | 'circuit' | 'assist'): string;
}

/**
 * One part of a step: a paragraph of the card (`tour.<step>.p<n>`) and the bit of demo that shows
 * it. Parts play one at a time, when the user moves to them.
 */
export interface TourPart {
  demo?(ctx: DemoCtx): Promise<void>;
}

export interface TourStep {
  id: string;
  chapter: number;
  /** Shortcut keys shown on the card. */
  keys?: string[];
  /** Card in the middle of the screen (intro, end): its text is `tour.<step>.body`. */
  centered?: boolean;
  /** How the sheet is framed before each part plays: close-up on the hand-drawn board, or the page. */
  view?: 'close' | 'page';
  parts?: TourPart[];
}

export const CHAPTERS = 6;

// ---------- sheet geometry ----------

/** Where a port of an element is on the sheet (unrotated symbols, as the demo places them). */
export function portAt(project: Project, id: Id | undefined, portId: string): Point | null {
  const el = id ? project.elements[id] : undefined;
  const p = el && getSymbol(el.symbolId)?.ports.find((q) => q.id === portId);
  return el && p ? { x: el.x + p.x, y: el.y + p.y } : null;
}

/** Centre of an element's symbol. */
export function centreOf(project: Project, id: Id | undefined): Point | null {
  const el = id ? project.elements[id] : undefined;
  const def = el && getSymbol(el.symbolId);
  return el && def ? { x: el.x + def.width / 2, y: el.y + def.height / 2 } : null;
}

/** A rectangle of the current sheet, in client pixels. */
export const sheetRect =
  (minX: number, minY: number, maxX: number, maxY: number): SpotFn =>
  (app) => {
    const a = app.toClient(minX, minY);
    const b = app.toClient(maxX, maxY);
    return { left: a.x, top: a.y, width: b.x - a.x, height: b.y - a.y };
  };

/** The elements of the current sheet, framed. */
const drawingSpot: SpotFn = (app) => {
  const p = app.editor.project;
  const els = Object.values(p.elements).filter((e) => e.sheetId === app.editor.sheetId);
  if (!els.length) return null;
  let [minX, minY, maxX, maxY] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const e of els) {
    const d = getSymbol(e.symbolId);
    minX = Math.min(minX, e.x);
    minY = Math.min(minY, e.y);
    maxX = Math.max(maxX, e.x + (d?.width ?? 10));
    maxY = Math.max(maxY, e.y + (d?.height ?? 10));
  }
  return sheetRect(minX - 6, minY - 6, maxX + 14, maxY + 8)(app);
};

const titleBlockSpot: SpotFn = (app) => {
  const p = app.editor.project;
  const s = p.sheets[app.editor.sheetId];
  if (!s) return null;
  const tb = pageLayout(p, s).titleBlock;
  return sheetRect(tb.x, tb.y, tb.x + tb.w, tb.y + tb.h)(app);
};

/** Close-up on the top-left of the sheet, where the hand-drawn board goes. */
export const CLOSE_VIEW = { minX: 12, minY: 14, maxX: 150, maxY: 145 };

const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r(null)));

const p = (c: DemoCtx) => c.app.editor.project;

/** Open a menu (unless it is open) and point at one of its items. */
async function menuItem(c: DemoCtx, menu: string, item: string, caption?: MessageKey, submenu?: string) {
  if (!document.querySelector(`[data-testid="${item}"]`)) {
    // The menu may already be open (a previous part), with another submenu showing.
    if (!submenu || !document.querySelector(`[data-testid="${submenu}"]`)) {
      await c.click(`[data-testid="${menu}"]`);
      await c.wait(250);
    }
    if (submenu) {
      await c.click(`[data-testid="${submenu}"]`);
      await c.wait(250);
    }
  }
  c.spot(`[data-testid="${item}"]`, caption);
  await c.wait(1200);
}

/** The project information dialog, open and filled in as the first part left it. */
async function infoDialog(c: DemoCtx) {
  if (!document.querySelector('#m-title')) {
    await c.click('[data-testid="menu-file"]');
    await c.wait(250);
    await c.click('[data-testid="project-info"]');
    await c.wait(300);
  }
  const typed: Array<[string, 'title' | 'owner' | 'address']> = [
    ['#m-title', 'title'],
    ['#m-ownerName', 'owner'],
    ['#m-installationAddress', 'address'],
  ];
  for (const [sel, k] of typed)
    if (!document.querySelector<HTMLInputElement>(sel)?.value) c.fill(sel, c.sample(k));
}

/**
 * A cell of the lighting circuit added from the table (the row with 1.5 mm²), else of the first
 * row: its code depends on the order of the circuits.
 */
function lightingCell(kind: 'ib' | 'length' | 'drop'): string {
  const select = [
    ...document.querySelectorAll<HTMLSelectElement>('.circuit-table [data-testid^="section-"]'),
  ].find((el) => el.value === '1.5');
  const code = select?.dataset.testid?.slice('section-'.length);
  return code ? `.circuit-table [data-testid="${kind}-${code}"]` : `.circuit-table [data-testid^="${kind}-"]`;
}

/** The sidebar unfolded with this section open (the user may have folded it). */
function sideSection(c: DemoCtx, id: SideSection) {
  const s = c.app.sidebar();
  if (!s.folded && s.open[id]) return;
  c.app.setSidebar({ ...s, folded: false, flyout: null, open: { ...s.open, [id]: true } });
}

/** Wire two ports of elements the demo placed. */
async function connect(c: DemoCtx, a: string, pa: string, b: string, pb: string) {
  const from = portAt(p(c), c.memo[a], pa);
  const to = portAt(p(c), c.memo[b], pb);
  if (from && to) await c.wire([from, to]);
}

export const TOUR_STEPS: TourStep[] = [
  // 1 — Start a project
  { id: 'intro', chapter: 1, centered: true },
  {
    id: 'workspace',
    chapter: 1,
    view: 'page',
    parts: [
      ...(
        [
          ['.toolbar', 'tour.area.toolbar'],
          ['.palette', 'tour.area.palette'],
          ['.stage .canvas', 'tour.area.canvas'],
        ] as Array<[string, MessageKey]>
      ).map(([sel, caption]) => ({
        async demo(c: DemoCtx) {
          c.spot(sel, caption);
        },
      })),
      {
        async demo(c) {
          sideSection(c, 'selection');
          c.spot('.sidebar', 'tour.area.inspector');
          await c.wait(1200);
          c.spot('.sidebar [data-section="assistant"] .side-sec-head', 'tour.area.assistant');
        },
      },
      {
        async demo(c) {
          c.app.setSidebar({ ...c.app.sidebar(), folded: false, flyout: null });
          await c.wait(100);
          c.spot('[data-testid="side-fold"]');
          await c.click('[data-testid="side-fold"]');
          await c.wait(400);
          c.spot('.side-rail', 'tour.area.rail');
          await c.wait(900);
          await c.click('[data-testid="side-icon-sheet"]');
          await c.wait(200);
          c.spot('.side-flyout');
          await c.wait(1500);
          await c.click('[data-testid="side-fold"]');
          await c.wait(300);
          c.spot('.sidebar');
        },
      },
      {
        async demo(c) {
          c.spot('.sheet-tabs', 'tour.area.sheets');
          await c.wait(1200);
          c.spot('footer.status', 'tour.area.status');
        },
      },
    ],
  },
  {
    id: 'start',
    chapter: 1,
    view: 'page',
    parts: [
      { demo: (c) => menuItem(c, 'menu-file', 'new-from-template', 'tour.cap.template') },
      { demo: (c) => menuItem(c, 'menu-file', 'survey-mode', 'tour.cap.survey') },
      { demo: (c) => menuItem(c, 'menu-file', 'load-demo', 'tour.cap.example') },
      {
        async demo(c) {
          c.closeMenus();
          await menuItem(c, 'menu-ai', 'ai-board', 'tour.cap.aiBoard');
        },
      },
    ],
  },
  {
    id: 'info',
    chapter: 1,
    view: 'page',
    parts: [
      {
        async demo(c) {
          if (!document.querySelector('#m-title')) {
            await c.click('[data-testid="menu-file"]');
            await c.wait(250);
            await c.click('[data-testid="project-info"]');
            await c.wait(300);
          }
          c.spot('.dialog');
          await c.type('#m-title', c.sample('title'));
          await c.type('#m-ownerName', c.sample('owner'));
          await c.type('#m-installationAddress', c.sample('address'));
        },
      },
      {
        async demo(c) {
          await infoDialog(c);
          c.spot('#m-standard', 'tour.cap.standard');
          await c.choose('#m-standard', 'rgie');
        },
      },
      {
        async demo(c) {
          await infoDialog(c);
          c.spot('#m-locale', 'tour.cap.locale');
          await c.wait(300);
        },
      },
      {
        async demo(c) {
          await infoDialog(c);
          c.spot('#m-icc', 'tour.cap.icc');
          await c.wait(300);
        },
      },
      {
        async demo(c) {
          await infoDialog(c);
          await c.click('[data-testid="project-info-save"]');
          await c.wait(200);
          c.spot(titleBlockSpot, 'tour.cap.titleBlock');
        },
      },
    ],
  },

  // 2 — Draw the single-line diagram by hand
  {
    id: 'palette',
    chapter: 2,
    view: 'close',
    parts: [
      {
        async demo(c) {
          c.app.setPaletteFolded(false);
          await c.wait(100);
          c.spot('.palette');
          await c.wait(700);
          c.spot('.palette .search', 'tour.cap.search');
        },
      },
      {
        async demo(c) {
          c.spot('.palette');
          c.memo.supply = (await c.place('iec.supply', { x: 40, y: 30 })) ?? '';
          c.app.editor.select([]);
          c.spot(sheetRect(32, 24, 62, 46), 'tour.cap.tag');
        },
      },
    ],
  },
  {
    id: 'place',
    chapter: 2,
    view: 'close',
    keys: ['R', 'M', 'Esc'],
    parts: [
      {
        async demo(c) {
          c.spot('.stage .canvas');
          c.memo.rcd = (await c.place('iec.rcd', { x: 40, y: 55 })) ?? '';
          c.memo.breaker = (await c.place('iec.breaker', { x: 40, y: 90 })) ?? '';
          c.memo.socket = (await c.place('iec.socket', { x: 40, y: 115 })) ?? '';
          c.app.editor.select([]);
          c.spot(drawingSpot);
        },
      },
      {
        async demo(c) {
          const at = centreOf(p(c), c.memo.socket);
          if (!at) return;
          c.spot(drawingSpot);
          await c.sheetClick(at);
          await c.key('R');
          c.app.editor.rotate();
          await c.wait(700);
          await c.key('{mod}Z');
          c.app.editor.history.undo();
          await c.wait(300);
          await c.key('Esc');
          c.app.editor.select([]);
        },
      },
    ],
  },
  {
    id: 'wire',
    chapter: 2,
    view: 'close',
    keys: ['W', 'Backspace', 'Esc'],
    parts: [
      {
        async demo(c) {
          c.spot('[data-testid="tool-wire"]');
          await connect(c, 'supply', 'out', 'rcd', 'in');
          c.spot(drawingSpot);
        },
      },
      {
        async demo(c) {
          c.spot(drawingSpot);
          await connect(c, 'rcd', 'out', 'breaker', 'in');
          await connect(c, 'breaker', 'out', 'socket', 'in');
          c.app.editor.setTool({ kind: 'select' });
        },
      },
      {
        async demo(c) {
          c.app.editor.setTool({ kind: 'select' });
          c.spot('[data-testid="tool-wire"]');
          await c.key('W');
          await c.key('Backspace');
          await c.key('Esc');
        },
      },
    ],
  },
  {
    id: 'inspector',
    chapter: 2,
    view: 'close',
    keys: ['V'],
    parts: [
      {
        async demo(c) {
          const at = centreOf(p(c), c.memo.breaker);
          if (!at) return;
          sideSection(c, 'selection');
          c.spot(drawingSpot);
          await c.sheetClick(at);
          await c.wait(300);
          c.spot('.sidebar [data-section="selection"]');
        },
      },
      {
        async demo(c) {
          const at = centreOf(p(c), c.memo.breaker);
          sideSection(c, 'selection');
          if (at && !c.app.editor.selection.has(c.memo.breaker ?? '')) await c.sheetClick(at);
          c.spot('.sidebar [data-section="selection"]');
          await c.type('#f-ratingA', '20', { commit: true });
          await c.choose('#f-curve', 'C');
          await c.wait(400);
          c.spot(drawingSpot, 'tour.cap.label');
        },
      },
    ],
  },
  {
    id: 'edit',
    chapter: 2,
    view: 'close',
    keys: ['{mod}Z', '{mod}D', '←↑→↓', 'Del'],
    parts: [
      {
        async demo(c) {
          const from = centreOf(p(c), c.memo.socket);
          if (!from) return;
          c.spot(drawingSpot);
          await c.drag(from, { x: from.x + 15, y: from.y + 10 });
        },
      },
      {
        async demo(c) {
          c.spot(drawingSpot);
          await c.key('{mod}Z');
          c.app.editor.history.undo();
          await c.wait(300);
          c.app.editor.select([]);
        },
      },
      {
        async demo(c) {
          c.spot(drawingSpot);
          await c.drag({ x: 25, y: 20 }, { x: 72, y: 132 });
          await c.wait(900);
          c.app.editor.select([]);
        },
      },
    ],
  },

  // 3 — The circuit table: the fast way
  {
    id: 'circuits',
    chapter: 3,
    view: 'page',
    parts: [
      {
        async demo(c) {
          if (c.app.panel() !== 'circuits') await c.click('[data-testid="toggle-circuits"]');
          await nextFrame();
          c.app.fitPage();
          c.spot('.bottom-panel');
        },
      },
      {
        async demo(c) {
          c.spot('.circuit-table table');
          await c.type('.circuit-table [data-testid^="label-"]', c.sample('circuit'), { commit: true });
          await c.wait(300);
          c.spot(drawingSpot);
        },
      },
    ],
  },
  {
    id: 'addCircuit',
    chapter: 3,
    view: 'page',
    parts: [
      {
        async demo(c) {
          if (c.app.panel() !== 'circuits') c.app.setPanel('circuits');
          await nextFrame();
          c.spot('.circuit-table .panel-actions');
          await c.choose('.circuit-table .panel-actions > select', 'lights');
          await c.click('[data-testid="add-circuit"]');
          await c.wait(400);
          c.spot(drawingSpot);
        },
      },
      {
        async demo(c) {
          c.spot('.circuit-table .panel-actions');
          await c.choose('.circuit-table .panel-actions > select', 'appliance');
          await c.click('[data-testid="add-circuit"]');
          await c.wait(400);
          c.spot(drawingSpot);
        },
      },
    ],
  },
  {
    id: 'calc',
    chapter: 3,
    view: 'page',
    parts: [
      {
        async demo(c) {
          c.spot(lightingCell('ib'), 'tour.cap.ib');
          await c.wait(300);
        },
      },
      {
        async demo(c) {
          const sel = lightingCell;
          c.spot('.circuit-table table');
          await c.type(sel('length'), '28', { commit: true });
          await c.wait(300);
          c.spot(sel('drop'), 'tour.cap.drop');
        },
      },
      {
        async demo(c) {
          c.spot('.circuit-table table');
          await c.wait(300);
        },
      },
    ],
  },
  {
    id: 'renumber',
    chapter: 3,
    view: 'page',
    parts: [
      {
        async demo(c) {
          await c.click('[data-testid="renumber"]');
          await c.wait(300);
          c.spot('.circuit-table td.code', 'tour.cap.codes');
        },
      },
      {
        async demo(c) {
          c.spot(drawingSpot);
          await c.wait(300);
        },
      },
    ],
  },

  {
    id: 'assistant',
    chapter: 3,
    view: 'page',
    parts: [
      {
        async demo(c) {
          sideSection(c, 'assistant');
          await nextFrame();
          c.spot('[data-testid="menu-ai"]');
          await c.wait(900);
          c.spot('.sidebar [data-section="assistant"]', 'tour.cap.assistant');
        },
      },
      {
        async demo(c) {
          sideSection(c, 'assistant');
          await nextFrame();
          if (document.querySelector('[data-testid="assist-text"]')) {
            c.spot('.sidebar [data-section="assistant"]');
            await c.type('[data-testid="assist-text"]', c.sample('assist'));
            c.spot('[data-testid="assist-send"]', 'tour.cap.assistSend');
          } else c.spot('[data-testid="assist-signin"]', 'tour.cap.assistSignIn');
        },
      },
      {
        async demo(c) {
          sideSection(c, 'assistant');
          await nextFrame();
          c.fill('[data-testid="assist-text"]', '');
          c.spot('.sidebar [data-section="assistant"]');
          await c.wait(300);
        },
      },
    ],
  },

  // 4 — The check
  {
    id: 'checks',
    chapter: 4,
    view: 'page',
    parts: [
      {
        async demo(c) {
          if (c.app.panel() !== 'checks') await c.click('[data-testid="toggle-checks"]');
          await c.wait(200);
          c.spot('.bottom-panel');
        },
      },
      {
        async demo(c) {
          if (c.app.panel() !== 'checks') c.app.setPanel('checks');
          await nextFrame();
          if (await c.click('.bottom-panel .finding-head')) c.spot('.bottom-panel .finding');
        },
      },
      {
        async demo(c) {
          c.spot('[data-testid="status-checks"]', 'tour.cap.statusChecks');
          await c.wait(300);
        },
      },
    ],
  },

  // 5 — The other diagrams
  {
    id: 'multiline',
    chapter: 5,
    parts: [
      {
        async demo(c) {
          c.app.setPanel(null);
          await menuItem(c, 'menu-diagram', 'cmd-multiline');
          await c.click('[data-testid="cmd-multiline"]');
          await c.wait(200);
          c.app.fitPage();
          await nextFrame();
          c.spot('.stage .canvas');
        },
      },
      {
        async demo(c) {
          c.spot('[data-testid="ml-banner"]', 'tour.cap.banner');
          await c.wait(300);
        },
      },
      {
        async demo(c) {
          await menuItem(c, 'menu-diagram', 'cmd-number-wires');
          await menuItem(c, 'menu-diagram', 'brand-0', undefined, 'menu-catalogue');
          await c.wait(1000);
          c.spot('[data-testid="cmd-board-layout"]');
          await c.wait(1000);
          c.closeMenus();
          c.spot('.stage .canvas');
        },
      },
    ],
  },
  {
    id: 'sheets',
    chapter: 5,
    parts: [
      {
        async demo(c) {
          c.spot('.sheet-tabs');
          await c.click('[data-testid="add-sheet"]');
          await c.wait(200);
          c.spot('.menu-list', 'tour.cap.sheetKinds');
        },
      },
      {
        async demo(c) {
          c.closeMenus();
          await c.click('[data-testid="sheet-tab-0"]');
          await c.wait(150);
          c.app.fitPage();
          c.spot('.sheet-tabs');
        },
      },
    ],
  },

  // 6 — Save and hand over
  {
    id: 'save',
    chapter: 6,
    view: 'page',
    keys: ['{mod}S'],
    parts: [
      {
        async demo(c) {
          c.closeMenus();
          c.spot('[data-testid="menu-file"]');
          await c.key('{mod}S');
        },
      },
      { demo: (c) => menuItem(c, 'menu-file', 'revisions', 'tour.cap.revisions') },
      {
        async demo(c) {
          await menuItem(c, 'menu-file', 'export-pdf-full', 'tour.cap.exportFull', 'menu-export');
          await menuItem(c, 'menu-file', 'report-quote', 'tour.cap.quote', 'menu-reports');
        },
      },
      {
        async demo(c) {
          c.closeMenus();
          c.spot('[data-testid="account"]', 'tour.cap.cloud');
          await c.wait(300);
        },
      },
    ],
  },
  { id: 'done', chapter: 6, centered: true },
];
